// features/workstations/dashboard-sections/outcome-calendar.js
// ═══════════════════════════════════════════════════════════
// OUTCOME CALENDAR HEATMAP — month-grid of day P&L
// ═══════════════════════════════════════════════════════════
// Tier C / analytics. Pure-data widget. Aggregates closed
// trades by exit date, renders a 7-column month grid with
// each day tinted sage (green days) or terra (red days), plus
// intensity by magnitude.
//
// Click a day → drawer shows the individual trades from that
// session. The edge-check widget — scan the month, find the
// clusters, drill into what happened.
//
// Month nav via prev/next arrows. No date picker until we see
// real users asking for one.
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Eyebrow,
  SerifHeadline,
  SerifSummary,
  SectionStrip,
  PillRow,
  PillTable,
  PractitionerLens,
} from '../../../components/widgets/primitives';

const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Current month/year in ET
function currentYearMonthET() {
  const parts = new Date().toLocaleDateString('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).split('-');
  return {
    year: parseInt(parts[0], 10),
    month: parseInt(parts[1], 10),
  };
}

function prevMonth(y, m) {
  if (m === 1) return { year: y - 1, month: 12 };
  return { year: y, month: m - 1 };
}
function nextMonth(y, m) {
  if (m === 12) return { year: y + 1, month: 1 };
  return { year: y, month: m + 1 };
}

// Tint intensity based on P&L magnitude (log scale clamp)
function intensityForPnl(pnl, maxAbs) {
  if (!pnl || !maxAbs) return 0;
  const ratio = Math.abs(pnl) / maxAbs;
  // 3 buckets: light / mid / strong
  if (ratio < 0.25) return 1;
  if (ratio < 0.60) return 2;
  return 3;
}

// ═══════════════════════════════════════════════════════════
// SECTION
// ═══════════════════════════════════════════════════════════
export default function OutcomeCalendarSection({ workstation, userId }) {
  const today = useMemo(() => currentYearMonthET(), []);
  const [view, setView] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const load = useCallback(async () => {
    if (!workstation?.id || !userId) return;
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({
        workstation_id: workstation.id,
        user_id: userId,
        year: String(view.year),
        month: String(view.month),
      });
      const res = await fetch(`/api/workstations/outcome-calendar?${qs}`, { cache: 'no-store' });
      const payload = res.ok ? await res.json() : null;
      if (!res.ok) setError(payload?.error || `HTTP ${res.status}`);
      setData(payload || null);
      setSelectedDate(null); // reset drawer on month change
    } catch (e) {
      setError(e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [workstation?.id, userId, view.year, view.month]);

  useEffect(() => { load(); }, [load]);

  const monthLabel = `${MONTH_NAMES[view.month - 1]} ${view.year}`;
  const isCurrentMonth = view.year === today.year && view.month === today.month;

  return (
    <section className="ws-outcomecal">
      {/* Header + nav */}
      <Eyebrow parts={['outcome calendar', monthLabel]} />

      {loading && <div className="ws-outcomecal-loading">Loading trades…</div>}

      {!loading && data && (
        <OutcomeCalendarBody
          data={data}
          view={view}
          isCurrentMonth={isCurrentMonth}
          today={today}
          onPrev={() => setView(prevMonth(view.year, view.month))}
          onNext={() => setView(nextMonth(view.year, view.month))}
          onPickToday={() => setView(today)}
          selectedDate={selectedDate}
          onSelectDate={setSelectedDate}
        />
      )}

      {!loading && error && !data && (
        <div className="ws-outcomecal-error">{error}</div>
      )}
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// BODY
// ═══════════════════════════════════════════════════════════
function OutcomeCalendarBody({
  data, view, isCurrentMonth, today,
  onPrev, onNext, onPickToday,
  selectedDate, onSelectDate,
}) {
  const totals = data.totals || {};
  const days = Array.isArray(data.days) ? data.days : [];

  // Index days by date for O(1) cell lookup
  const byDate = {};
  let maxAbs = 0;
  for (const d of days) {
    byDate[d.date] = d;
    if (Math.abs(d.total_pnl) > maxAbs) maxAbs = Math.abs(d.total_pnl);
  }

  // Build the grid — weeks starting Sunday
  const cells = buildMonthGrid(view.year, view.month);

  const winRate = typeof totals.win_rate === 'number'
    ? `${Math.round(totals.win_rate * 100)}% win rate`
    : 'no resolved trades';

  const pnlTone = (totals.total_pnl || 0) > 0 ? 'sage'
    : (totals.total_pnl || 0) < 0 ? 'terra' : 'steel';
  const pnlText = fmtPnl(totals.total_pnl);

  const selectedDay = selectedDate ? byDate[selectedDate] : null;

  return (
    <>
      <SerifHeadline>
        {totals.trade_count > 0
          ? `${totals.trade_count} trades · ${pnlText}`
          : 'No closed trades this month.'}
      </SerifHeadline>
      <SerifSummary>
        {totals.trade_count > 0
          ? `${winRate}${totals.total_r ? ` · ${totals.total_r >= 0 ? '+' : ''}${totals.total_r.toFixed(1)}R total` : ''}.`
          : 'Log a trade and close it — the calendar fills itself from there.'}
      </SerifSummary>

      {/* Month nav */}
      <div className="ws-outcomecal-nav">
        <button type="button" className="ws-outcomecal-nav-btn" onClick={onPrev} title="Previous month">←</button>
        <span className="ws-outcomecal-nav-month">{MONTH_NAMES[view.month - 1]} {view.year}</span>
        <button
          type="button"
          className="ws-outcomecal-nav-btn"
          onClick={onNext}
          disabled={isCurrentMonth}
          title={isCurrentMonth ? 'No future trades yet' : 'Next month'}
        >→</button>
        {!isCurrentMonth && (
          <button type="button" className="ws-outcomecal-nav-today" onClick={onPickToday}>
            today
          </button>
        )}
      </div>

      {/* Heatmap grid */}
      <div className="ws-outcomecal-grid">
        {/* Day-of-week header */}
        {DAY_LABELS.map((lbl, i) => (
          <div key={`h-${i}`} className="ws-outcomecal-cell ws-outcomecal-header-cell">
            {lbl}
          </div>
        ))}

        {/* Day cells */}
        {cells.map((cell, i) => {
          if (cell === null) {
            return <div key={i} className="ws-outcomecal-cell ws-outcomecal-empty-cell" />;
          }
          const day = byDate[cell.date];
          const isToday = cell.date === data.today;
          const isSelected = cell.date === selectedDate;
          const tone = day ? (day.total_pnl > 0 ? 'sage' : day.total_pnl < 0 ? 'terra' : 'steel') : null;
          const intensity = day ? intensityForPnl(day.total_pnl, maxAbs) : 0;
          return (
            <button
              key={i}
              type="button"
              className={[
                'ws-outcomecal-cell',
                'ws-outcomecal-day-cell',
                tone ? `ws-outcomecal-day-${tone}` : '',
                intensity ? `ws-outcomecal-intensity-${intensity}` : '',
                isToday ? 'ws-outcomecal-today' : '',
                isSelected ? 'ws-outcomecal-selected' : '',
                !day ? 'ws-outcomecal-no-trades' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => day && onSelectDate(isSelected ? null : cell.date)}
              disabled={!day}
              title={day
                ? `${cell.date} · ${day.trade_count} trade${day.trade_count === 1 ? '' : 's'} · ${fmtPnl(day.total_pnl)}`
                : cell.date}
            >
              <span className="ws-outcomecal-day-num">{cell.day}</span>
              {day && (
                <span className={`ws-outcomecal-day-pnl ws-outcomecal-day-pnl-${tone}`}>
                  {fmtPnlShort(day.total_pnl)}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Drawer — selected day's trades */}
      {selectedDay && (
        <SectionStrip
          label={`${selectedDay.date} · ${selectedDay.trade_count} trade${selectedDay.trade_count === 1 ? '' : 's'}`}
          count={selectedDay.trade_count}
        >
          <PillTable>
            {selectedDay.trades.map((t) => {
              const pnl = Number(t.realized_pnl) || 0;
              const tone = pnl > 0 ? 'sage' : pnl < 0 ? 'terra' : 'steel';
              const r = Number(t.realized_r);
              const rText = Number.isFinite(r) && r !== 0
                ? ` · ${r >= 0 ? '+' : ''}${r.toFixed(1)}R`
                : '';
              return (
                <PillRow
                  key={t.id}
                  keyText={`${t.ticker || '?'} ${t.direction || ''}${t.setup_type ? ` [${t.setup_type}]` : ''}`.trim()}
                  valueText={`${fmtPnl(pnl)}${rText}`}
                  tone={tone}
                />
              );
            })}
          </PillTable>
        </SectionStrip>
      )}

      {/* Totals summary (always visible at bottom) */}
      {totals.trade_count > 0 && (
        <PractitionerLens label="month summary">
          <strong>{totals.trade_count}</strong> trade{totals.trade_count === 1 ? '' : 's'} ·{' '}
          <span className={`ws-outcomecal-tone-${pnlTone}`}>{pnlText}</span>
          {typeof totals.total_r === 'number' && totals.total_r !== 0 && (
            <> · {totals.total_r >= 0 ? '+' : ''}{totals.total_r.toFixed(1)}R</>
          )}
          {' · '}
          {totals.wins}W / {totals.losses}L{totals.breakeven > 0 ? ` / ${totals.breakeven}BE` : ''}
          {typeof totals.win_rate === 'number' && (
            <> · {Math.round(totals.win_rate * 100)}% win rate</>
          )}
        </PractitionerLens>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════
// GRID BUILDER — 6-row Sunday-start calendar for a month
// ═══════════════════════════════════════════════════════════
function buildMonthGrid(year, month) {
  // First day of month (local / UTC — close enough for
  // day-of-week purposes; DST shifts don’t flip weekdays)
  const first = new Date(Date.UTC(year, month - 1, 1));
  const firstDow = first.getUTCDay(); // 0=Sun
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();

  const cells = [];
  // Leading blanks for days before the 1st
  for (let i = 0; i < firstDow; i++) cells.push(null);
  // Day cells
  for (let d = 1; d <= lastDay; d++) {
    cells.push({
      day: d,
      date: `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
    });
  }
  // Trailing blanks to fill out the last row to 7
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

// ─── Formatters ───
function fmtPnl(pnl) {
  if (pnl == null || Number.isNaN(pnl)) return '—';
  const sign = pnl > 0 ? '+' : pnl < 0 ? '−' : '';
  const abs = Math.abs(pnl);
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}k`;
  return `${sign}$${abs.toFixed(0)}`;
}

function fmtPnlShort(pnl) {
  if (pnl == null || Number.isNaN(pnl) || pnl === 0) return '';
  const sign = pnl > 0 ? '+' : '−';
  const abs = Math.abs(pnl);
  if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(0)}k`;
  if (abs >= 100) return `${sign}${Math.round(abs / 10) * 10}`;
  return `${sign}${Math.round(abs)}`;
}
